import { Box, Typography } from '@mui/material'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Trans, useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePurchaseFlowConfig, usePurchaseFlowRealEstateProperty, useTargetOrderUser } from 'components/pages/PurchaseFlow/_main/contexts'
import { useUserData } from 'components/contexts/UserDataContext'

import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import { AddressMap } from 'components/common/AdressMap'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { CreateUpdateProperty } from '../CreateUpdateProperty'
import { GoogleAPIPlace } from 'models/purchaseFlow'
import { Label } from 'components/common/Label'
import { LoadMapScriptWrapper } from 'components/common/AdressMap/LoadMapScriptWrapper'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { SearchProperty } from '../SearchProperty'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import Stack from '@mui/material/Stack'
import { StepWrapper } from 'components/pages/PurchaseFlow/common/StepWrapper'
import { SystemMessage } from 'components/common/SystemMessage'
import TransitionAppear from 'components/common/TransitionAppear/TransitionAppear'
import { isAddressValid } from 'utils/validators'
import { logAnalyticsEvent } from 'utils/analytics'
import { useAuth0 } from 'utils/auth'

/**
 * @component
 * AddressStep component responsible for handling the address selection step in the purchase flow.
 *
 * @example
 * <AddressStepController />
 */
export const AddressStepController: React.FC = () => {
  const { i18n } = useTranslation()
  const { t } = useTranslation('order', { keyPrefix: 'step_address' })
  const { roles } = useAuth0()

  const {
    isPropertyPrefilled,
    requestedPropertyData,
    getPropertyQuery,
    selectedProperty,
    setPropertyName,
    resetRealEstateProperty,
    isAddressManually,
    isUpdatePropertyAddressFlow,
    isPropertyPrefilledFlow,
    setIsUpdatePropertyAddressFlow,
  } = usePurchaseFlowRealEstateProperty()

  const {
    getAddressConfig,
    selectedAssignmentPlace,
    sessionId,
    setSelectedAssignmentPlace,
  } = usePurchaseFlowConfig()

  const { targetUser, adminSelectedUserEmail } = useTargetOrderUser()
  const { currentUserWorkspace } = useUserData()

  const [isOpenCreateProperty, setIsOpenCreateProperty] = useState<boolean>(false)

  const addressIsValid = useMemo(() => isAddressValid(selectedAssignmentPlace), [selectedAssignmentPlace])

  const [showAddressUpdateHint, setShowAddressUpdateHint] = useState<boolean>(false)

  // Fetch the address configuration
  useEffect(() => {
    if (!sessionId) return
    if (roles.isAdmin && !adminSelectedUserEmail) return
    if (!getAddressConfig.isIdle) return

    getAddressConfig.mutate({
      sessionId,
      email: adminSelectedUserEmail,
    })
  }, [adminSelectedUserEmail, getAddressConfig, roles.isAdmin, sessionId])

  /** Logs entering this page with the selected category information. */
  useEffect(() => {
    logAnalyticsEvent('enters_order_address_selection_screen', {})
  }, [])

  useEffect(() => {
    setShowAddressUpdateHint(!!isPropertyPrefilledFlow && !!requestedPropertyData?.address)
  }, [isPropertyPrefilledFlow, requestedPropertyData?.address])

  const { initialAddress, initialMarker } = useMemo(() => {
    if (selectedAssignmentPlace) {
      return {
        initialAddress: selectedAssignmentPlace.formatted_address,
        initialMarker: selectedProperty?.address.coordinates ?? selectedAssignmentPlace.geometry.location
      }
    }

    if (isPropertyPrefilled && requestedPropertyData?.address) {
      const { street, streetNumber, city, postalCode, country, coordinates } = requestedPropertyData.address
      return {
        initialAddress: `${street} ${streetNumber}, ${city}, ${postalCode}, ${country}`,
        initialMarker: coordinates,
      }
    }

    return {
      initialAddress: undefined,
      initialMarker: undefined,
    }
  }, [isPropertyPrefilled, requestedPropertyData?.address, selectedAssignmentPlace, selectedProperty?.address.coordinates])

  const prefilled = useRef<boolean>(false)

  const handleMapOnChange = useCallback((newGooglePlace: GoogleAPIPlace | null, isInitialLookup: boolean) => {
    if (!isInitialLookup) setSelectedAssignmentPlace(newGooglePlace)
    if (isInitialLookup && isPropertyPrefilled && !prefilled.current) {
      prefilled.current = true
      setSelectedAssignmentPlace(newGooglePlace)
    }
  }, [isPropertyPrefilled, setSelectedAssignmentPlace])

  return (
    <TransitionAppear visible>

      <LoadMapScriptWrapper
        language={i18n.language}
        countryCode={targetUser?.country}
      >
        {/* Currently only allow client to use property search */}
        {roles.isClient ?
          <>
            {/* Property selection */}
            <StepWrapper
              title={
                <Trans
                  t={t}
                  i18nKey={isUpdatePropertyAddressFlow || isPropertyPrefilledFlow ? 'preselected_property_title_workspace' : 'property_selection_title_workspace'}
                  values={{ workspaceName: currentUserWorkspace?.name }}
                  components={{ workspace: <Box component="span" color={GRAY_700} /> }}
                />
              }
              subtitle={t(isUpdatePropertyAddressFlow || isPropertyPrefilledFlow ? 'preselected_property_text' : 'property_selection_text')}
              titleSx={{ maxWidth: '40%' }}
            >

              <Stack flexDirection="row" justifyContent="space-between" gap="6rem" height="100%">

                <Stack gap=".4rem" width="40%" position="relative" overflow="auto" paddingBottom="4rem">

                  {/* Property search and select */}
                  <Stack>
                    <SearchProperty />

                    {showAddressUpdateHint &&
                      <Stack marginTop="1.2rem" marginBottom="1.2rem">
                        <Label
                          text={
                            <Trans t={t} i18nKey="update_address_hint">
                              <span
                                style={{ color: GRAY_900, cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => {
                                  setIsUpdatePropertyAddressFlow(true)
                                  setShowAddressUpdateHint(false)
                                }}
                              >
                                &nbsp;
                              </span>
                            </Trans>
                          }
                        />
                      </Stack>
                    }
                  </Stack>

                  {/* Property name input */}

                  {(!selectedProperty || isUpdatePropertyAddressFlow) &&
                    <>
                      {!isUpdatePropertyAddressFlow && <MUIDivider>
                        {t('or')}
                      </MUIDivider>}

                      {/* Create property */}
                      {isOpenCreateProperty || isUpdatePropertyAddressFlow ?
                        <SectionedBorderBox
                          title={t(isUpdatePropertyAddressFlow ? 'update_property' : 'new_property')}
                          onIconClick={() => {
                            if (isUpdatePropertyAddressFlow) return

                            setIsOpenCreateProperty(false)
                            setSelectedAssignmentPlace(null)
                            setPropertyName('')
                          }}
                          customIcon={<CloseRoundedIcon fontSize='large' sx={{ color: GRAY_900 }} />}
                        >
                          <CreateUpdateProperty
                            addressIsValid={addressIsValid}
                            handleMapOnChange={handleMapOnChange}
                            selectedPlace={selectedAssignmentPlace}
                            setSelectedPlace={setSelectedAssignmentPlace}
                          />
                        </SectionedBorderBox>
                        :
                        <Stack width="100%" alignItems="center">
                          <MUIButton
                            size="md"
                            type="secondaryBorder"
                            isIconButton
                            sx={{ padding: '.8rem 1.2rem' }}
                            startIcon={<AddCircleOutlineRoundedIcon fontSize="large" />}
                            onClick={() => {
                              resetRealEstateProperty()
                              setIsOpenCreateProperty(!isOpenCreateProperty)
                            }}
                          >
                            {t('create_property_button')}
                          </MUIButton>
                        </Stack>
                      }
                    </>
                  }

                </Stack>

                {/* Map */}
                <Stack
                  sx={{
                    top: 0,
                    right: 4,
                    left: '47%', // So that it has some space on the right side
                    width: '50%',
                    height: '70vh',
                    paddingTop: 4.8,
                    overflow: 'hidden',
                    position: 'absolute',
                  }}
                >

                  {isAddressManually &&
                    <Box paddingBottom=".8rem">
                      <Label text={t('manually_drag_map_pin_note')} />
                    </Box>
                  }

                  <AddressMap
                    height="100%"
                    draggable={!selectedProperty || isUpdatePropertyAddressFlow}
                    language={i18n.language}
                    countryCode={targetUser?.country}
                    initialAddress={initialAddress}
                    initialMarker={initialMarker}
                    isMapPinAdjusted={!isAddressManually}
                    handleChange={handleMapOnChange}
                  />
                </Stack>

              </Stack>

            </StepWrapper>
          </>
          :
          <StepWrapper title={t('title')} subtitle={t('text')}>

            {/* Google Map for Address Selection - Will be deleted after allowing property selection. Currently for ADMIN. */}
            <Stack>

              {getPropertyQuery.isError &&
                <SystemMessage
                  variant="warning"
                  message={t('property_prefill_fail')}
                  sx={{ mb: 4 }}
                />
              }

              <Stack flexDirection="row" justifyContent="space-between" gap="6rem">

                <Stack width="50%" marginBottom="2rem" gap={2} overflow="auto" height="45rem">
                  <SectionedBorderBox title={t('new_property')}>
                    <CreateUpdateProperty
                      addressIsValid={addressIsValid}
                      handleMapOnChange={handleMapOnChange}
                      selectedPlace={selectedAssignmentPlace}
                      setSelectedPlace={setSelectedAssignmentPlace}
                    />
                  </SectionedBorderBox>
                </Stack>

                <Stack width="100%" height="100%" gap="1rem">

                  {isAddressManually &&
                    <Stack gap=".4rem">
                      <Typography variant="text-xs" fontWeight={400} color={GRAY_700}>{t('manually_drag_map_pin_note')}</Typography>
                    </Stack>
                  }

                  <AddressMap
                    height="40rem"
                    draggable={!selectedProperty || isUpdatePropertyAddressFlow}
                    language={i18n.language}
                    countryCode={targetUser?.country}
                    initialAddress={initialAddress}
                    initialMarker={initialMarker}
                    isMapPinAdjusted={!isAddressManually}
                    handleChange={handleMapOnChange}
                  />
                </Stack>

              </Stack>

            </Stack>

          </StepWrapper>
        }
      </LoadMapScriptWrapper>

    </TransitionAppear>
  )
}
